import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import Modal from "@/components/Common/Modal";
import type {CampaignDashboardSerializer, CampaignOwnerDetailSerializer} from "@/generated/DjangoTypes";
import {useIsAgency} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useCampaignFilterStore from "@/store/campaignFilter";
import {campaignDeleteAtom} from "@/store/jotaiAtoms";
import type {TPaginated} from "@/store/types";
import useUserStore from "@/store/user";
import {useMutation, type InfiniteData} from "@tanstack/react-query";
import {useNavigate, useParams} from "@tanstack/react-router";
import type {AxiosResponse} from "axios";
import {useAtom} from "jotai";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

type TPaginatedCampaignData = InfiniteData<AxiosResponse<TPaginated<CampaignDashboardSerializer>>>;

const CampaignDeleteDialog: FC = () => {
  const {t} = useTranslation("common");
  const navigate = useNavigate();
  const {campaignId, companyId} = useParams({strict: false});
  const isAgency = useIsAgency();
  const [campaignToBeDeleted, setCampaignToBeDeleted] = useAtom(campaignDeleteAtom);
  const user = useUserStore(state => state.user);
  const searchParams = useCampaignFilterStore(state => state.searchParams);

  const shouldRedirectToWelcome = () => {
    const open = queryClient.getQueryData<TPaginatedCampaignData>(
      COMPANY_KEYS.qCompanyCampaigns({
        companyId: Number(companyId),
        status: "open",
      }),
    );
    const active = queryClient.getQueryData<TPaginatedCampaignData>(
      COMPANY_KEYS.qCompanyCampaigns({
        companyId: Number(companyId),
        status: "active",
      }),
    );
    const complete = queryClient.getQueryData<TPaginatedCampaignData>(
      COMPANY_KEYS.qCompanyCampaigns({
        companyId: Number(companyId),
        status: "complete",
      }),
    );
    const draft = queryClient.getQueryData<TPaginatedCampaignData>(
      COMPANY_KEYS.qCompanyCampaigns({
        companyId: Number(companyId),
        status: "drafts",
      }),
    );
    const paused = queryClient.getQueryData<TPaginatedCampaignData>(
      COMPANY_KEYS.qCompanyCampaigns({
        companyId: Number(companyId),
        status: "paused",
      }),
    );

    return (
      open?.pages[0].data.results.length === 0 &&
      active?.pages[0].data.results.length === 0 &&
      complete?.pages[0].data.results.length === 0 &&
      draft?.pages[0].data.results.length === 0 &&
      paused?.pages[0].data.results.length === 0
    );
  };

  const {mutateAsync: deleteCampaign, isPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignDelete(campaignToBeDeleted === "ALL_DRAFTS" ? -1 : campaignToBeDeleted?.id!),
    mutationFn: async () => {
      if (!campaignToBeDeleted || campaignToBeDeleted === "ALL_DRAFTS") {
        return;
      }

      const campaign = queryClient.getQueryData<CampaignOwnerDetailSerializer>(
        CAMPAIGN_KEYS.qCampaign(campaignToBeDeleted.id),
      );

      if (campaignToBeDeleted!.draft) {
        await client.delete(CAMPAIGN_KEYS.mCampaign(campaignToBeDeleted.id)[0]);
        capturePostHogEvent(PostHogEvents.DraftCampaignDeleted, {
          campaignId: Number(campaignToBeDeleted.id),
          companyId: companyId ? Number(companyId) : undefined,
          campaignType: campaign ? (campaign.ugc ? "ugc" : campaign.tiktok ? "tiktok" : "instagram") : undefined,
        });
      } else {
        await client.post(CAMPAIGN_KEYS.mCampaignDelete(campaignToBeDeleted.id)[0]);
        capturePostHogEvent(PostHogEvents.PublishedCampaignDeleted, {
          campaignId: Number(campaignToBeDeleted.id),
          companyId: companyId ? Number(companyId) : undefined,
          campaignType: campaign ? (campaign.ugc ? "ugc" : campaign.tiktok ? "tiktok" : "instagram") : undefined,
        });
      }

      if (!campaignId || isAgency) {
        return;
      }

      if (user.user_type === "admin") {
        navigate({to: "/campaigns"});
      } else if (shouldRedirectToWelcome()) {
        navigate({to: "/welcome"});
      } else {
        navigate({
          to: "/company/$companyId/dashboard",
          params: {companyId: Number(companyId)},
          resetScroll: false,
        });
      }
    },
    onSuccess: () => {
      const statuses = ["active", "complete", "drafts", "open", "paused"] as const;

      [
        CAMPAIGN_KEYS.qCampaignList(searchParams),
        ...statuses.map(status => COMPANY_KEYS.qCompanyCampaigns({companyId: Number(companyId), status})),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      setCampaignToBeDeleted(null);
    },
  });

  const {mutateAsync: deleteAllDrafts} = useMutation({
    mutationKey: COMPANY_KEYS.mCompanyDraftCampaignsDelete(Number(companyId)),
    mutationFn: async () => {
      await client.post(COMPANY_KEYS.mCompanyDraftCampaignsDelete(Number(companyId))[0]);

      if (isAgency) {
        return;
      }

      if (shouldRedirectToWelcome()) {
        navigate({to: "/welcome"});
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: COMPANY_KEYS.qCompanyCampaigns({companyId: Number(companyId), status: "drafts"}),
      });
      setCampaignToBeDeleted(null);
    },
  });

  return (
    <Modal
      onClose={() => setCampaignToBeDeleted(null)}
      confirmButtonText={t("Yes, I'm sure")}
      isLoading={isPending}
      shouldCloseOnConfirm={false}
      onConfirm={async () => (campaignToBeDeleted === "ALL_DRAFTS" ? await deleteAllDrafts() : await deleteCampaign())}
      modalTitle={t("Campaign deletion")}>
      <span className="text-lg">
        {t(
          campaignToBeDeleted === "ALL_DRAFTS"
            ? "deleteAllDraftsConfirm"
            : "Are you sure you want delete this campaign?",
        )}
      </span>
      <span className="text-lg">{t("All data will be erased")}.</span>
    </Modal>
  );
};

export default CampaignDeleteDialog;
